import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DEVICES = [
  { id: 'garmin', name: 'Garmin', model: 'Forerunner 965', icon: 'Watch', color: '#22c55e', battery: 78, connected: true, lastSync: '2 min ago' },
  { id: 'apple', name: 'Apple Watch', model: 'Series 9', icon: 'Smartphone', color: '#3b82f6', battery: 62, connected: true, lastSync: '1 min ago' },
  { id: 'whoop', name: 'Whoop', model: '4.0', icon: 'Activity', color: '#8b5cf6', battery: 91, connected: true, lastSync: 'Just now' },
  { id: 'polar', name: 'Polar', model: 'H10', icon: 'Heart', color: '#ef4444', battery: 45, connected: false, lastSync: '18 min ago' },
];

const BatteryIcon = ({ level, color }) => {
  const bars = Math.ceil(level / 25);
  return (
    <div className="flex items-center gap-0.5">
      <div className="flex gap-0.5 items-end h-3">
        {[1, 2, 3, 4]?.map(b => (
          <div
            key={b}
            className="w-1 rounded-sm transition-all"
            style={{
              height: `${b * 25}%`,
              backgroundColor: b <= bars ? (level < 20 ? '#ef4444' : color) : '#e2e8f0',
              minHeight: '3px',
            }}
          />
        ))}
      </div>
      <span className="text-xs font-medium ml-0.5" style={{ color: level < 20 ? '#ef4444' : '#94a3b8' }}>{level}%</span>
    </div>
  );
};

const DeviceStatusBar = ({ onSync }) => {
  const [syncing, setSyncing] = useState(null);

  const handleSync = (deviceId) => {
    setSyncing(deviceId);
    setTimeout(() => setSyncing(null), 2500);
    onSync?.(deviceId);
  };

  return (
    <div className="bg-card rounded-2xl p-4 border border-border/40">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon name="Wifi" size={16} className="text-muted-foreground" />
          <h3 className="font-semibold text-foreground text-sm">Connected Devices</h3>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          {DEVICES?.filter(d => d?.connected)?.length}/{DEVICES?.length} online
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {DEVICES?.map((device, idx) => (
          <motion.div
            key={device?.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.07 }}
            className={`relative rounded-xl p-3 border transition-all ${
              device?.connected
                ? 'border-border/40 bg-muted/20' :'border-border/20 bg-muted/10 opacity-70'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${device?.color}15` }}>
                <Icon name={device?.icon} size={16} color={device?.connected ? device?.color : '#94a3b8'} />
              </div>
              <div className={`w-2 h-2 rounded-full mt-1 ${device?.connected ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            </div>

            <div className="mb-1.5">
              <div className="text-xs font-semibold text-foreground leading-tight">{device?.name}</div>
              <div className="text-xs text-muted-foreground">{device?.model}</div>
            </div>

            <BatteryIcon level={device?.battery} color={device?.color} />

            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">{device?.lastSync}</span>
              <motion.button
                onClick={() => handleSync(device?.id)}
                disabled={!device?.connected || syncing === device?.id}
                className="w-6 h-6 rounded-lg flex items-center justify-center transition-colors hover:bg-muted/50 disabled:opacity-40"
                whileTap={{ scale: 0.9 }}
              >
                <motion.div
                  animate={{ rotate: syncing === device?.id ? 360 : 0 }}
                  transition={{ duration: 0.8, repeat: syncing === device?.id ? Infinity : 0, ease: 'linear' }}
                >
                  <Icon name="RefreshCw" size={12} className="text-muted-foreground" />
                </motion.div>
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default DeviceStatusBar;
